import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from './aws-exports';
import { ThemeProvider } from './ThemeContext';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Patients from './pages/Patients';
import Notifications from './pages/Notifications';
import Staff from './pages/Staff';
import Hospitals from './pages/Hospitals';
import Appointments from './pages/Appointments';
import AuditLogs from './pages/AuditLogs';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import './App.css';

Amplify.configure(awsConfig);

export const UserContext = React.createContext(null);

function ProtectedLayout({ children, user }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <UserContext.Provider value={user}>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-page)' }}>
        <Sidebar
          user={user}
          mobileOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
        />
        {/* Main content — offset by sidebar width on desktop */}
        <div className="main-content" style={{ flex: 1, marginLeft: 220, minWidth: 0 }}>
          <TopBar user={user} onMenuOpen={() => setMobileOpen(true)} />
          <div style={{ padding: '20px 20px' }}>
            {children}
          </div>
        </div>
      </div>
    </UserContext.Provider>
  );
}

function ProtectedRoute({ children, allowedRoles, user }) {
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      await getCurrentUser();
      const session = await fetchAuthSession();
      const claims  = session.tokens?.idToken?.payload || {};
      setUser({
        email:       claims.email || '',
        name:        claims['custom:staff_name'] || claims.name || '',
        role:        claims['custom:role'] || 'reception',
        hospital_id: claims['custom:hospital_id'] || '',
        staff_id:    claims['custom:staff_id'] || '',
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-sub)', fontSize: 14, background: 'var(--bg-page)' }}>
      Loading...
    </div>
  );

  if (!user) return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login onLogin={checkAuth} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );

  return (
    <ThemeProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/" element={<ProtectedLayout user={user}><Dashboard user={user} /></ProtectedLayout>} />
        <Route path="/patients" element={
          <ProtectedLayout user={user}>
            <ProtectedRoute allowedRoles={['super-admin', 'hospital-admin', 'reception', 'doctor']} user={user}>
              <Patients user={user} />
            </ProtectedRoute>
          </ProtectedLayout>
        } />
        <Route path="/notifications" element={
          <ProtectedLayout user={user}>
            <ProtectedRoute allowedRoles={['super-admin', 'hospital-admin', 'reception']} user={user}>
              <Notifications user={user} />
            </ProtectedRoute>
          </ProtectedLayout>
        } />
        <Route path="/appointments" element={
          <ProtectedLayout user={user}>
            <ProtectedRoute allowedRoles={['super-admin', 'hospital-admin', 'reception', 'doctor']} user={user}>
              <Appointments user={user} />
            </ProtectedRoute>
          </ProtectedLayout>
        } />
        <Route path="/staff" element={
          <ProtectedLayout user={user}>
            <ProtectedRoute allowedRoles={['super-admin', 'hospital-admin']} user={user}>
              <Staff user={user} />
            </ProtectedRoute>
          </ProtectedLayout>
        } />
        <Route path="/hospitals" element={
          <ProtectedLayout user={user}>
            <ProtectedRoute allowedRoles={['super-admin']} user={user}>
              <Hospitals user={user} />
            </ProtectedRoute>
          </ProtectedLayout>
        } />
        <Route path="/audit" element={
          <ProtectedLayout user={user}>
            <ProtectedRoute allowedRoles={['super-admin', 'hospital-admin']} user={user}>
              <AuditLogs user={user} />
            </ProtectedRoute>
          </ProtectedLayout>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ThemeProvider>
  );
}